/**
 * 授權來源的中文對照表（批104）。
 *
 * 為什麼要有這一份：`licenses.issued_via` 與 `license_addons.source` 一直是**直接印原字串**
 * （`LicensesView.vue` 的子證表印 `a.source`＝`admin`／`partner`…，主證那欄只判斷過
 * `'direct'` 一種）。批104 兩個值域各多一個 `redeem`，再不建表就會出現一個誰都看不懂的
 * 英文字 `redeem` 夾在中文表格裡。
 *
 * ★ 值域與後端 `db/models.py` 的欄位註解一一對應（`License.issued_via:453-466`、
 *   `LicenseAddon.source:573-575`）——**後端加值域時這裡要一起加**，否則畫面退回印英文鍵
 *   （`fallback` 刻意回原字串而不是「未知」：印得出鍵至少查得到，印「未知」什麼都沒有）。
 */

import type { ProductSpec } from '@/api/ls'
import { fmtModulesZh } from '@/lib/modules'
import { quotaLabel } from '@/lib/quotas'

/** `licenses.issued_via`：這張主證是怎麼來的。
 *
 * ★ 授權表的「狀態」欄是 `white-space: nowrap` 且整張表刻意壓到不橫捲，所以這裡的
 *   chip 字數要與既有的「直發」看齊——`redeem` 用兩個字的「兌換」（完整語意在 tooltip）。
 */
export const ISSUED_VIA_LABELS: Record<string, string> = {
  request: '申請核可',
  policy: '政策直簽',
  direct: '直發',
  system: '系統重簽',
  redeem: '兌換',
}

/** `license_addons.source`：這張子憑證是誰開的。 */
export const ADDON_SOURCE_LABELS: Record<string, string> = {
  admin: '我方後台',
  partner: '代理商',
  portal: '客戶自助',
  system: '系統重簽',
  redeem: '兌換碼',
}

/** 每個來源的一句說明（`title` 提示用；沒有對應就不給 tooltip）。 */
export const ISSUED_VIA_HINTS: Record<string, string> = {
  request: '走授權申請單核可後簽發',
  policy: '套裝的核發政策允許，確認申請時直接簽發',
  direct: '未經申請流程、由超級管理者直接簽發（已記稽核並發告警）',
  system: '系統重簽（換機／移轉／客戶合併／批次回填），不是一次銷售',
  redeem: '客戶使用兌換碼簽出的證',
}

export function issuedViaLabel(v: string | null | undefined): string {
  if (!v) return '—'
  return ISSUED_VIA_LABELS[v] ?? v
}

export function issuedViaHint(v: string | null | undefined): string | undefined {
  return v ? ISSUED_VIA_HINTS[v] : undefined
}

export function addonSourceLabel(v: string | null | undefined): string {
  if (!v) return '—'
  return ADDON_SOURCE_LABELS[v] ?? v
}

/** 兌換活動的三種效果（`redeem_campaigns.kind`）。 */
export const REDEEM_KIND_LABELS: Record<string, string> = {
  extend: '延長期限',
  trial: '功能試用',
  grant: '領取試用授權',
}

export function redeemKindLabel(v: string | null | undefined): string {
  if (!v) return '—'
  return REDEEM_KIND_LABELS[v] ?? v
}

// --- 加購（子憑證）的顯示（批110 從 `LicensesView` 搬出來共用）----------------

/** 加購的剩餘天數；`null`＝還沒蓋章（等待啟用）。 */
export function addonDaysLeft(a: { expires_at: string | null }): number | null {
  if (!a.expires_at) return null
  return Math.ceil((new Date(a.expires_at).getTime() - Date.now()) / 86400000)
}

/** 加購的**顯示**狀態：DB 只有 active|revoked|superseded，「已到期」是推導出來的。 */
export function addonStatusLabel(a: {
  status: string
  expires_at: string | null
}): string {
  if (a.status === 'revoked') return '已撤銷'
  if (a.status === 'superseded') return '已被取代'
  const d = addonDaysLeft(a)
  if (d === null) return '等待啟用'
  return d < 0 ? '已到期' : '生效中'
}

/**
 * 加購內容一行字。模組／配額的中文名查**該產品**的憑證設計（不同產品鍵完全不同），
 * 所以 spec 要由呼叫端帶進來（`store.productByKey(a.product)`）。
 */
export function addonSummary(
  a: {
    modules?: Record<string, string> | null
    quotas?: Record<string, number | null> | null
  },
  spec: ProductSpec | null,
): string {
  const parts: string[] = []
  const mods = fmtModulesZh(spec, a.modules ?? {})
  if (mods.length) parts.push(`模組 ${mods.map((m) => m.text).join('、')}`)
  if (a.quotas) {
    parts.push(...Object.entries(a.quotas).map(([k, v]) => `${quotaLabel(spec, k)} → ${v}`))
  }
  // ★ 批114：這裡原本另外印子證上的兩個一級數字欄。那兩個鍵批109 起就住在 `quotas` 裡，
  //   上面那一圈已經印過——留著等於同一件事印兩次（而且其中一次是硬編的產品字面）。
  return parts.join('；') || '—'
}

/**
 * 「＋加購」chip 的 tooltip：主證原值 → 有效值，以及最早到期日（批110 從 `LicensesView`
 * 搬出來，詳情 modal 也要用）。
 *
 * ★ 三種值刻意分開講：**未設定**（鍵缺席）／**不限**（值 null）／數字。全部混成 `?? '—'`
 *   會讓人以為「未設定」與「不限」是同一件事，而它們在產品端是完全相反的語意。
 */
export function quotaAddonHint(
  lic: {
    quotas?: Record<string, number | null> | null
    addon_sources?: { quotas?: Record<string, string | null> } | null
  },
  key: string,
  effective: number | null | undefined,
): string {
  const raw = lic.quotas ?? {}
  const from = !(key in raw) ? '未設定' : raw[key] === null ? '不限' : String(raw[key])
  const to = effective == null ? '不限' : String(effective)
  const until = lic.addon_sources?.quotas?.[key]
  const when = until ? `，最早到期 ${until.slice(0, 10)}` : '，尚未啟用（首次下發才起算）'
  return `含加購：主證 ${from} → 有效 ${to}${when}`
}

/** 模組版的同一件事（模組沒有數值，只講到期）。 */
export function moduleAddonHint(
  lic: { addon_sources?: { modules?: Record<string, string | null> } | null },
  key: string,
): string {
  const until = lic.addon_sources?.modules?.[key]
  return until ? `含加購，最早到期 ${until.slice(0, 10)}` : '含加購（尚未啟用，首次下發才起算）'
}
